import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/system';
import { ThemeProvider } from '@mui/material/styles';
import logo from '../assets/rounded_logo.svg';
import theme from '../styles/theme';
import PaginationControls from '../components/prospect/PaginationControls';
import TourSlotsDisplay from '../components/prospect/TourSlotsDisplay';
import PrimaryButton from '../components/PrimaryButton';
import TourConfirmationScreen from '../components/prospect/TourConfirmationScreen';
import EmailCollectionScreen from '../components/prospect/EmailCollectionScreen';
import QuestionnaireEngine from '../components/prospect/QuestionnaireEngine';
import axiosClient from '../services/axiosClient';
import ProspectTourSuccess from '../components/ProspectTourSuccess';
import { trackFeatureUsage } from '../services/FeatureTrackingService';

interface TourSlot {
  id: string;
  date: string;
  start_time_local: string;
  end_time_local: string;
  available_spots: number;
  status?: string;
}

interface Props {
  listing_id: string;
  grouped_by_date_tour_slots: Record<string, TourSlot[]>;
  preview: boolean;
  minimum_monthly_income: number;
  property_address: string;
}

const SLOTS_PER_PAGE = 5;
const LOCAL_STORAGE_Q_ANS_KEY = 'prospect_questionnaire_answers';


const SchedulingPlusScreening: React.FC<Props> = ({ listing_id, grouped_by_date_tour_slots, preview, minimum_monthly_income, property_address }) => {
  console.log("grouped_by_date_tour_slots", grouped_by_date_tour_slots)
  const isMobile = useMediaQuery('(max-width:600px)');
  // Use a dynamic localStorage key based on listing_id
  const LOCAL_STORAGE_KEY = `heyy-prop-${listing_id}`;

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const prospectIdFromUrl = urlParams.get('prospect_id');
  const isCancel = urlParams.get('cancel') === 'true';

  // Initialize currentScreen and selectedTimeSlot from localStorage if present
  const [currentScreen, setCurrentScreen] = useState(() => {
    // If this is a cancel operation, go directly to final confirmation (Screen 6)

    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return typeof parsed.currentScreen === 'number' ? parsed.currentScreen : 1;
      } catch {
        return 1;
      }
    }
    return 1;
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TourSlot | null>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.selectedTimeSlot || null;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [slotPage, setSlotPage] = useState(0);
  const [prospectEmail, setProspectEmail] = useState<string>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.prospectEmail || '';
      } catch {
        return '';
      }
    }
    return '';
  });
  const [existingProspect, setExistingProspect] = useState<any>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.existingProspect || null;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<Record<string, any> | null>(null);
  const [screeningResult, setScreeningResult] = useState<any>(null);
  const [submissionError, setSubmissionError] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [showAdCard, setShowAdCard] = useState(false);
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const onboardingFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showOnboardingForm && onboardingFormRef.current) {
      onboardingFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showOnboardingForm]);

  // Persist currentScreen and selectedTimeSlot to localStorage whenever either changes
  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({ currentScreen, selectedTimeSlot, prospectEmail, existingProspect })
    );
  }, [currentScreen, selectedTimeSlot, prospectEmail, existingProspect, LOCAL_STORAGE_KEY]);

  // Clear localStorage when reaching confirmation page (Screen 6)
  useEffect(() => {
    if (currentScreen === 6) {
      console.log('=== CLEARING LOCALSTORAGE AFTER CONFIRMATION ===');
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }, [currentScreen, LOCAL_STORAGE_KEY]);

  // Handle notification banner timing
  useEffect(() => {
    if (currentScreen === 1 && allSlots.length > 0) {
      // Show notification after 1 second
      const showTimer = setTimeout(() => {
        setShowNotification(true);
        setNotificationVisible(true);
      }, 1500);

      // Hide notification after 5 seconds
      const hideTimer = setTimeout(() => {
        setNotificationVisible(false);
        // Remove from DOM after animation completes
        setTimeout(() => {
          setShowNotification(false);
        }, 300);
      }, 8000);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [currentScreen]);

  // Show ad card with a soft fade-in after 2.5 seconds on confirmation screen
  useEffect(() => {
    if (currentScreen === 6 && screeningResult && screeningResult.screening_pass && !submissionError) {
      const adTimer = setTimeout(() => {
        setShowAdCard(true);
      }, 1900);

      return () => clearTimeout(adTimer);
    } else {
      setShowAdCard(false);
      setShowOnboardingForm(false);
    }
  }, [currentScreen, screeningResult, submissionError]);


  // Function to handle tour cancellation
  const handleTourCancel = async () => {
    if (!prospectIdFromUrl) return;

    console.log('=== AUTOMATICALLY CANCELLING TOUR ===');
    const payload = {
      prospect_id: prospectIdFromUrl,
    };

    try {
      const response = await axiosClient.patch(`/schedule-tour/${listing_id}/cancel`, payload);
      console.log('Tour cancel response:', response.data);
      // Tour is now cancelled
    } catch (error) {
      console.error('Error cancelling tour:', error);
      // Even if cancel fails, we still show the confirmation page
    }
  };

  // Flatten all slots into a single array with date info
  const allSlots: (TourSlot & { date: string })[] = Object.entries(grouped_by_date_tour_slots)
    .flatMap(([date, slots]) => slots.map(slot => ({ ...slot, date })));

  console.log("allSlots", allSlots)

  // Sort by date and start_time
  allSlots.sort((a, b) => {
    if (a.date < b.date) return -1;
    if (a.date > b.date) return 1;
    if (a.start_time_local < b.start_time_local) return -1;
    if (a.start_time_local > b.start_time_local) return 1;
    return 0;
  });

  console.log("allSlots sorted", allSlots)

  // Paginate
  const startIdx = slotPage * SLOTS_PER_PAGE;
  const endIdx = startIdx + SLOTS_PER_PAGE;
  const paginatedSlots = allSlots.slice(startIdx, endIdx);

  console.log("paginatedSlots", paginatedSlots)

  // Format time for display
  const formatTime = (timeString: string) => {
    console.log("TimeString is: " + timeString)
    // timeString is now in HH:MM format (e.g., "13:00")
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    console.log("converted to time: " + date)
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    console.log("dateString is: " + dateString)
    // Append time component to ensure date is interpreted in local timezone
    const date = new Date(dateString + 'T12:00:00');
    console.log("NEW converted to time: " + date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }))
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'schedule';
      case 'rescheduled':
        return 'reschedule';
      case 'cancelled':
        return 'cancel';
      default:
        return 'schedule';
    }
  };

  // Navigation handlers
  const handleShowMore = () => {
    if (endIdx < allSlots.length) setSlotPage(slotPage + 1);
  };
  const handleShowPrevious = () => {
    if (slotPage > 0) setSlotPage(slotPage - 1);
  };

  // Time slot select
  const handleTimeSlotSelect = (slot: TourSlot & { date: string }) => {
    setSelectedTimeSlot(slot);
  };

  const NoAvailabilityMessage = styled(Box)({
    textAlign: 'center',
    maxWidth: '600px',
    padding: '2rem',
  });

  // When users get an server error and refresh the page, they will see the rejection. When they should not
  useEffect(() => {
    if (currentScreen === 6 && !screeningResult && !submissionError) {
      setCurrentScreen(5);
    }
  }, [currentScreen, screeningResult, submissionError]);

  useEffect(() => {
    if (currentScreen === 5 && !questionnaireAnswers) {
      const saved = localStorage.getItem(LOCAL_STORAGE_Q_ANS_KEY);
      if (saved) {
        setQuestionnaireAnswers(JSON.parse(saved));
      }
    }
  }, [currentScreen, questionnaireAnswers]);

  const handleScreeningForExistingProspect = async (answers: Record<string, any>) => {
    if (!selectedTimeSlot || !prospectEmail || !existingProspect) return;

    console.log('=== EXISTING PROSPECT FAST TRACK SCREENING START ===');
    console.log('Existing prospect data:', existingProspect);
    console.log('New income answer:', answers.makes_enough_income);
    console.log('Current email:', prospectEmail);
    console.log('Selected tour slot:', selectedTimeSlot);

    // Use fast track endpoint for existing prospects with new tour booking
    const payload = {
      prospect_id: existingProspect.prospect_id,
      tour_slot_id: selectedTimeSlot.id,
      makes_enough_income: answers.makes_enough_income
    };

    console.log('Fast track screening payload:', payload);
    console.log('Sending to endpoint:', `/schedule-tour/${listing_id}/fast-track`);

    try {
      const response = await axiosClient.post(`/schedule-tour/${listing_id}/fast-track`, payload);
      console.log('Fast track screening response received:', response.data);
      console.log('Screening result - Pass:', response.data.screening_pass);
      console.log('Screening result - Reasons:', response.data.reasons);
      console.log('=== EXISTING PROSPECT FAST TRACK SCREENING END ===');

      setScreeningResult(response.data);
      setSubmissionError(false);
      setCurrentScreen(6);
    } catch (error) {
      console.error('Fast track screening error:', error);
      console.log('=== EXISTING PROSPECT FAST TRACK SCREENING FAILED ===');
      setSubmissionError(true);
      setCurrentScreen(6);
    }
  };

  const handleTourSubmission = async () => {
    if (!questionnaireAnswers || !selectedTimeSlot || !prospectEmail) return;

    console.log('=== NEW PROSPECT SCREENING START ===');
    console.log('Questionnaire answers:', questionnaireAnswers);
    console.log('Current email:', prospectEmail);
    console.log('Selected tour slot:', selectedTimeSlot);

    const payload = {
      prospect: { ...questionnaireAnswers, email: prospectEmail },
      listing_id,
      tour_slot_id: selectedTimeSlot.id,
    };

    console.log('New prospect screening payload:', payload);
    console.log('Sending to endpoint:', `/schedule-tour/${listing_id}`);

    try {
      const response = await axiosClient.post(`/schedule-tour/${listing_id}`, payload);
      console.log('Screening response received:', response.data);
      console.log('Screening result - Pass:', response.data.screening_pass);
      console.log('Screening result - Reasons:', response.data.reasons);
      console.log('=== NEW PROSPECT SCREENING END ===');

      setScreeningResult(response.data);
      setSubmissionError(false);
      setCurrentScreen(6);
    } catch (error) {
      console.error('Submission error:', error);
      console.log('=== NEW PROSPECT SCREENING FAILED ===');
      setSubmissionError(true);
      setCurrentScreen(6);
    }
  };

  const handleCustomerBypass = async () => {
    if (!selectedTimeSlot || !prospectEmail) return;

    console.log('=== CUSTOMER BYPASS - DIRECT TO CONFIRMATION ===');
    console.log('Customer data:', prospectEmail);
    console.log('Selected tour slot:', selectedTimeSlot);
    console.log('Is cancel:', isCancel);
    console.log('Prospect email from URL:', prospectEmail);

    // If this is a cancel operation and we have a prospect ID, call the cancel endpoint
    if (isCancel && prospectEmail) {
      console.log('=== CUSTOMER TOUR CANCEL ===');
      const payload = {
        prospect_id: prospectIdFromUrl,
      };
      try {
        const response = await axiosClient.patch(`/schedule-tour/${listing_id}/cancel`, payload);
        console.log('Customer cancel response:', response.data);
        setScreeningResult({
          screening_pass: true,
          reasons: [],
          prospect_id: prospectIdFromUrl
        });
        setSubmissionError(false);
        setCurrentScreen(6);
      } catch (error) {
        console.error('Customer cancel error:', error);
        setSubmissionError(true);
        setCurrentScreen(6);
      }
      return;
    }

    // For customers, skip all screening and go directly to confirmation
    setScreeningResult({
      screening_pass: true,
      reasons: [],
      prospect_id: prospectIdFromUrl
    });
    setSubmissionError(false);
    setCurrentScreen(6);
  };

  useEffect(() => {

    // Save the current margin
    const originalOverflowX = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    const originalMargin = document.body.style.margin;
    // Set margin to 0
    document.body.style.margin = '0';

    // Restore on unmount
    return () => {
      document.body.style.margin = originalMargin;
      document.body.style.overflowX = originalOverflowX;
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {/* Persistent header with logo and title */}
      <Box
        sx={{
          width: '90%',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '1rem' : '2rem',
          borderBottom: '1px solid #eee',
          mb: 0.5,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Heyy"
          sx={{
            height: 40,
            mr: 2,
          }}
        />
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#000' }}>
          {currentScreen === 1 ? 'Schedule Tour' : currentScreen === 2 ? 'Next Steps' : currentScreen === 3 ? 'Email' : currentScreen === 4 ? 'Questionnaire' : currentScreen === 5 ? 'Questions Done' : ''}
        </Typography>
      </Box>

      {currentScreen === 1 && (
        <Box
          sx={{
            minHeight: '100vh',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginTop: 0,
            padding: isMobile ? '1rem' : '2rem',
          }}
        >
          {allSlots.length === 0 ? (
            <NoAvailabilityMessage>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                We apologize, but the landlord hasn't set their availability yet.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Please check back later.
              </Typography>
            </NoAvailabilityMessage>
          ) : (
            <>
              {/* Paginated tour slots with date headers */}
              <TourSlotsDisplay
                slots={paginatedSlots}
                selectedSlotId={selectedTimeSlot?.id || null}
                onSelectSlot={handleTimeSlotSelect}
                formatDate={formatDate}
                formatTime={formatTime}
              />
              {/* Pagination controls */}
              <PaginationControls
                onShowPrevious={handleShowPrevious}
                onShowMore={handleShowMore}
                disablePrevious={slotPage === 0}
                disableMore={endIdx >= allSlots.length}
              />
              <PrimaryButton
                onClick={() => setCurrentScreen(2)}
                disabled={!selectedTimeSlot}
                sx={{
                  mt: 4,
                  ml: isMobile ? -4 : -9,
                  padding: '12px 30px',
                  fontSize: '1.15rem',
                  minWidth: isMobile ? '60%' : '25%',
                  fontWeight: 600,
                }}
              >
                Continue
              </PrimaryButton>
            </>
          )}
        </Box>
      )}

      {currentScreen === 2 && (
        <TourConfirmationScreen
          selectedTimeSlot={selectedTimeSlot}
          formatDate={formatDate}
          formatTime={formatTime}
          onChangeTourTime={() => setCurrentScreen(1)}
          onContinue={() => setCurrentScreen(3)}
          isMobile={isMobile}
          isCustomer={false}
          propertyAddress={property_address}
        />
      )}
      {currentScreen === 3 && (
        <EmailCollectionScreen
          onContinue={async (email, existingProspectData) => {
            setProspectEmail(email);
            setExistingProspect(existingProspectData);

            if (existingProspectData) {
              console.log('Existing prospect found:', existingProspectData);
              console.log('Prospect ID:', existingProspectData.prospect_id);
              console.log('Original tour:', existingProspectData.original_tour);

              // If prospect exists and has an original tour, use rescheduling endpoint
              if (existingProspectData.original_tour) {
                // Use rescheduling endpoint for existing prospect with previous tour
                const payload = {
                  prospect_id: existingProspectData.prospect_id,
                  new_tour_slot_id: selectedTimeSlot?.id,
                };

                console.log('Rescheduling existing prospect tour:', payload);
                try {
                  const response = await axiosClient.patch(`/schedule-tour/${listing_id}/reschedule`, payload);
                  console.log('Reschedule response:', response.data);
                  setScreeningResult({
                    screening_pass: true,
                    reasons: [],
                    prospect_id: existingProspectData.prospect_id,
                    tour_status: response.data.tour_status
                  });
                  setSubmissionError(false);
                  setCurrentScreen(6);
                } catch (error) {
                  console.error('Reschedule error:', error);
                  setSubmissionError(true);
                  setCurrentScreen(6);
                }
                return;
              }
              // If prospect exists but no original tour, go to questionnaire to collect income info
              else {
                setCurrentScreen(4);
                return;
              }
            } else {
              console.log('No existing prospect found for this listing');
              // If no prospect exists, go through full screening questionnaire
              setCurrentScreen(4);
            }
          }}
          onBack={() => setCurrentScreen(2)}
          isMobile={isMobile}
          listingId={listing_id}
        />
      )}
      {currentScreen === 4 && (
        <QuestionnaireEngine
          minimum_monthly_income={minimum_monthly_income}
          onComplete={answers => {
            setQuestionnaireAnswers(answers);

            // If existing prospect, perform screening immediately
            if (existingProspect) {
              handleScreeningForExistingProspect(answers);
            } else {
              // New prospect, go to questions done screen
              setCurrentScreen(5);
            }
          }}
          localStorageKey={LOCAL_STORAGE_Q_ANS_KEY}
          existingProspect={existingProspect}
        />
      )}
      {currentScreen === 5 && (
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Questions Done
          </Typography>
          <PrimaryButton
            sx={{ mt: 3, minWidth: 200 }}
            onClick={async () => {
              console.log(questionnaireAnswers)
              console.log("--")
              console.log(selectedTimeSlot)
              handleTourSubmission();
            }}
          >
            Submit
          </PrimaryButton>
        </Box>
      )}
      {currentScreen === 6 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          {/* Screening rejection */}
          {!submissionError && screeningResult && !screeningResult.screening_pass && (
            <Box sx={{ px: isMobile ? 2 : 4, textAlign: 'left' }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Apologies, you do not meet this landlord's criteria.
              </Typography>
              {screeningResult.reasons?.length > 0 && (
                <ul style={{ color: 'red', marginTop: 16 }}>
                  {screeningResult.reasons.map((reason: string) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </Box>
          )}

          {/* Submission error */}
          {submissionError && (
            <Box sx={{ px: isMobile ? 2 : 4, textAlign: 'left' }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Sorry, something went wrong on our end. Please submit the request again.
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                If this issue keeps happening, contact{' '}
                <a href="mailto:rich@heyyproperty.com">rich@heyyproperty.com</a> and we will
                make the booking for you.<br />
                Please include ListingNumber: <b>{listing_id}</b> in the email.
              </Typography>
              <PrimaryButton sx={{ mt: 2, minWidth: 200 }} onClick={() => setCurrentScreen(5)}>
                Try Again
              </PrimaryButton>
            </Box>
          )}

          {/* Tour confirmed — happy path */}
          {!submissionError && screeningResult?.screening_pass && (
              <ProspectTourSuccess
              isMobile={isMobile}
              propertyAddress={property_address}
              selectedTimeSlot={selectedTimeSlot}
              formatDate={formatDate}
              formatTime={formatTime}
              showAdCard={showAdCard}
              showOnboardingForm={showOnboardingForm}
              onGetStarted={() => {
                trackFeatureUsage(null, 'Renter-Ad-0-Clicked');
                setShowOnboardingForm(true);
              }}
              onboardingFormRef={onboardingFormRef}
            />
          )}
        </Box>
      )}
    </ThemeProvider>
  );
};

export default SchedulingPlusScreening;